<template>
  <b-modal
    header-close-content=""
    id="auto-mapping-modal"
    modal-class="auto-mapping-modal"
    body-class="pt-0 pb-0"
    :title="$t('Automapping.Automapping')"
    @ok="executeAutoMapping"
    @close="clear"
    @show="init"
    size="xl"
    data-cy="channel-creator-auto-mapping-modal"
    no-close-on-backdrop
    no-close-on-esc>
    <div class="form-group mb-3 auto-mapping-dialog">
      <b-container fluid>
        <b-row>
          <b-col cols="6" class="px-0 my-auto pr-3">
            <b-form-group label-class="font-weight-bold" :label="$t('CatalogsExisting')" class="mb-0">
              <div class="d-flex">
                <b-dropdown class="dropdown-default grow max-width-menu mr-1" data-cy="channel-creator-auto-mapping-catalog-name-ddn">
                  <template #button-content>
                    <text-with-fallback-display
                      v-tooltip.ellipsis="catalogName"
                      :source="autoMappingCatalog"
                      :language="primaryLanguage"
                      field="name"></text-with-fallback-display>
                  </template>
                  <b-dropdown-item
                    v-for="catalog in sortedLoadedCatalogs"
                    :key="catalog[language].catalogId"
                    @click="selectCatalog(catalog[language].catalogId)"
                    :active="catalogName === catalog[language].name">
                    <text-with-fallback-display
                      v-tooltip.ellipsis.mouse="getCatalogName(catalog)"
                      :source="catalog"
                      :language="primaryLanguage"
                      field="name"></text-with-fallback-display>
                  </b-dropdown-item>
                </b-dropdown>
                <b-button class="btn-icon mono" v-tooltip="$t('OpenCatalogGroups')" v-b-modal.auto-mapping-manage-catalogs>
                  <i class="mdp-Open"></i>
                </b-button>
              </div>
            </b-form-group>
          </b-col>

          <b-col cols="6" class="px-0 my-auto pl-3">
            <b-form-group label-class="font-weight-bold" :label="$t('MappingProjectsExisting')" class="mb-0">
              <div class="d-flex">
                <b-dropdown class="dropdown-default grow max-width-menu mr-1" data-cy="channel-creator-auto-mapping-project-ddn">
                  <template #button-content>
                    <span class="ellipsis" v-tooltip.ellipsis="selectedMappingProjectText">{{ selectedMappingProjectText }}</span>
                  </template>
                  <b-dropdown-item
                    v-for="project in sortedMappingProjectList"
                    :key="project.catalogMappingProjectId"
                    :active="selectedMappingProjectText === project.name"
                    @click="selectMappingProjectFromList(project.catalogMappingProjectId)">
                    <span class="ellipsis" v-tooltip.ellipsis.mouse="project.name">
                      {{ project.name }}
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
                <b-button class="btn-icon mono" v-tooltip="$t('new')" v-b-modal.auto-mapping-create-mapping-project>
                  <i class="mdp-Plus"></i>
                </b-button>
                <b-button class="btn-icon mono" v-tooltip="$t('OpenMappingProject')" v-b-modal.auto-mapping-open-mapping-project>
                  <i class="mdp-Open"></i>
                </b-button>
                <b-btn
                  class="btn-icon mono"
                  :disabled="isAdvancedSettingsDisabled"
                  v-tooltip="$t('Automapping.AdvancedMappingSettings')"
                  v-b-modal.advanced-auto-mapping-settings>
                  <i class="mdp-Preferences"></i>
                </b-btn>
              </div>
            </b-form-group> </b-col
        ></b-row>
        <b-row class="mt-3">
          <b-col cols="12" class="px-0 my-auto">
            <h5 class="mb-1 informationbase">{{ $t("Automapping.MappingScriptTitle") }}</h5>
          </b-col>
          <b-col cols="12" class="px-0 my-auto">
            <div class="infoText">
              <Alert class="w-100" with-icon show variant="secondary" data-cy="article-attributes-empty-state">
                {{ $t("Automapping.MappingScriptInfoText") }}
              </Alert>
            </div>
          </b-col>
        </b-row>

        <b-row class="mt-0" v-if="autoMappingDetails && autoMappingDetails.length > 0">
          <b-col
            cols="6"
            :class="index % 2 === 0 ? 'pr-3 px-0' : 'pl-3 px-0'"
            v-for="(autoMappingDetail, index) in autoMappingDetails"
            :key="autoMappingDetail.label">
            <b-form-group :label="$t('Automapping.' + autoMappingDetail.label)" label-class="font-weight-bold" class="mb-0 mt-3">
              <div class="d-flex">
                <b-dropdown class="dropdown-default max-width-menu grow">
                  <template #button-content>
                    <span class="ellipsis" v-tooltip.ellipsis="$t('Automapping.MappingScript_' + autoMappingDetail.selectedMappingScript.toLowerCase())">
                      {{ $t("Automapping.MappingScript_" + autoMappingDetail.selectedMappingScript.toLowerCase()) }}
                    </span>
                  </template>
                  <b-dropdown-item
                    v-for="mappingScriptName in getSortedMappingScriptNames(autoMappingDetail.mappingScriptNames)"
                    :key="mappingScriptName.label"
                    :active="mappingScriptName.toLowerCase() === autoMappingDetail.selectedMappingScript.toLowerCase()"
                    @click="selectMappingScript(autoMappingDetail, mappingScriptName)">
                    <span class="ellipsis" v-tooltip.ellipsis.mouse="$t('Automapping.MappingScript_' + mappingScriptName.toLowerCase())">
                      {{ $t("Automapping.MappingScript_" + mappingScriptName.toLowerCase()) }}
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-form-group>
          </b-col></b-row
        >
        <b-row class="checkbox-area">
          <b-col cols="12" class="px-0 my-auto">
            <b-checkbox
              v-model="existingAttributeGroupMappingsIsDeleted"
              @change="confirmDelete"
              data-cy="channel-creator-auto-mapping-delete-field-mappings-cbx"
              >{{ $t("Automapping.FieldMappingsDeleteExisting") }}</b-checkbox
            >
          </b-col>
          <b-checkbox
            class="ml-3"
            :disabled="!existingAttributeGroupMappingsIsDeleted"
            v-model="existingGlobalMappingsIsDeleted"
            data-cy="channel-creator-auto-mapping-delete-global-mappings-cbx">
            {{ $t("Automapping.FieldMappingsDeleteExistingIncludeGlobals") }}
          </b-checkbox></b-row
        >
      </b-container>

      <manage-catalogs modal-name="auto-mapping-manage-catalogs" :includedCatalogTypes="[0, 1]" @selectedCatalog="selectCatalog" dialogMode="open" />
      <mapping-project-creation-dialog
        v-model:show-dialog="showCreateMappingDialog"
        modal-name="auto-mapping-create-mapping-project"
        @createdMappingProject="createdMappingProject"
        v-model:mappingProjects="mappingProjectList"
        :catalogId="catalogId"
        :catalogName="catalogName"></mapping-project-creation-dialog>
      <mapping-project-selection-dialog
        v-model:show-dialog="showOpenProjectSelectorDialog"
        modal-name="auto-mapping-open-mapping-project"
        :catalogId="catalogId"
        @selectMappingProject="selectMappingProject"></mapping-project-selection-dialog>
      <advanced-auto-mapping-settings-dialog
        modal-name="advanced-auto-mapping-settings"
        v-if="!isAdvancedSettingsDisabled"
        :catalogMappingTargetId="mappingProject.catalogMappingTargetId"></advanced-auto-mapping-settings-dialog>
    </div>
    <template #modal-footer="{ ok }">
      <b-btn @click="ok" :disabled="isInvalid" variant="primary" data-cy="channel-creator-auto-mapping-run-btn">{{ $t("Automapping.RunAutoMapping") }}</b-btn>
    </template>
  </b-modal>
</template>

<script>
import _ from "lodash";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import manageCatalogs from "../ManageCatalogs.vue";
import mappingProjectCreationDialog from ".././BasemappingTab/MappingProjectCreationDialog.vue";
import mappingProjectSelectionDialog from ".././BasemappingTab/OpenMappingProjectDialog.vue";
import textWithFallbackDisplay from "./../../../../src/common/components/TextWithFallbackDisplay.vue";
import advancedAutoMappingSettingsDialog from "./AdvancedAutoMappingSettingsDialog.vue";
import catalogApi from "../../../services/catalogs";
import automappingApi from "../../../services/automapping";
import { useToast } from "@common/composable/toast";
import { naturalSort } from "@util/naturalSort";
import Alert from "@common/components/Alert.vue";

export default {
  inject: ["$getConnectionId"],
  components: {
    manageCatalogs,
    mappingProjectCreationDialog,
    mappingProjectSelectionDialog,
    textWithFallbackDisplay,
    advancedAutoMappingSettingsDialog,
    Alert
  },
  setup() {
    const { createToast } = useToast();

    return { createToast };
  },
  data() {
    return {
      autoMappingCatalog: null,
      mappingProject: null,
      existingAttributeGroupMappingsIsDeleted: false,
      existingGlobalMappingsIsDeleted: false,
      mappingProjectList: [],
      autoMappingDetails: {},
      showCreateMappingDialog: false,
      showOpenProjectSelectorDialog: false
    };
  },
  computed: {
    ...mapState("channelCreator", ["catalogs", "mappingProjects", "selectedTargetCatalogId", "modified"]),
    ...mapState("metadata", ["contentLanguages"]),
    ...mapGetters("channelCreator", ["selectedTargetCatalog", "selectedMappingProject"]),
    dirty: {
      get() {
        return this.modified.baseMapping;
      },
      set(nv) {
        this.markDirty({ section: "baseMapping", dirty: nv });
      }
    },
    loadedCatalogs() {
      return this.catalogs.filter((x) => x[this.language]?.catalogId);
    },
    sortedLoadedCatalogs() {
      return naturalSort(this.loadedCatalogs, (elem) => elem[this.language]?.name);
    },
    sortedMappingProjectList() {
      return naturalSort(this.mappingProjectList, "name");
    },
    primaryLanguage() {
      return this.contentLanguages?.enabled[0];
    },
    language() {
      return this.primaryLanguage?.languageCode;
    },
    catalogId() {
      return this.autoMappingCatalog && this.language ? this.autoMappingCatalog[this.language]?.catalogId : null;
    },
    catalogName() {
      return this.autoMappingCatalog && this.language ? this.autoMappingCatalog[this.language]?.name : null;
    },
    isAdvancedSettingsDisabled() {
      return !this.mappingProject || !this.mappingProject.targetSystem || this.mappingProject.targetSystem != "Office";
    },
    selectedMappingProjectText() {
      return this?.mappingProject?.name ?? "-";
    },
    isInvalid() {
      return !this.autoMappingCatalog || !this.mappingProject;
    }
  },
  methods: {
    ...mapActions("channelCreator", ["addMappingProject", "copyMappingProject"]),
    ...mapMutations("channelCreator", ["markDirty"]),
    selectCatalog(selectedCatalogId) {
      this.autoMappingCatalog = this.catalogs.find((ca) => ca[this.language]?.catalogId == selectedCatalogId);
    },
    selectMappingProjectFromList(catalogMappingProjectId) {
      if (this.mappingProject.catalogMappingProjectId != catalogMappingProjectId) {
        this.mappingProject = this.mappingProjectList.find((prj) => prj.catalogMappingProjectId == catalogMappingProjectId);
      }
    },
    selectMappingProject: async function (selectedProject) {
      if (selectedProject && selectedProject.catalogMappingProjectId) {
        if (this.mappingProjectList.some((prj) => prj.catalogMappingProjectId == selectedProject.catalogMappingProjectId)) {
          this.mappingProject = this.mappingProjectList.find((prj) => prj.catalogMappingProjectId == selectedProject.catalogMappingProjectId);
        } else {
          this.mappingProjectList.push(selectedProject);
          const sortKeyFinder = (x) => x?.name?.toUpperCase();
          this.mappingProjectList = _.sortBy(this.mappingProjectList, sortKeyFinder);
          this.mappingProject = selectedProject;
        }
      } else {
        this.mappingProject = null;
      }
    },
    executeAutoMapping: async function (bvModalEvent) {
      if (this.dirty) {
        if (!confirm(this.$t("confirmNavigation"))) {
          bvModalEvent.preventDefault();
          return;
        } else this.dirty = false;
      }
      const selectedMappingScripts = this.autoMappingDetails.map((scr) => ({
        index: scr.index,
        mappingScriptName: scr.selectedMappingScript
      }));
      const mappingGeneratorSettings = {
        catalogId: this.catalogId,
        catalogMappingProjectId: this.mappingProject.catalogMappingProjectId,
        catalogMappingTargetId: this.mappingProject.catalogMappingTargetId,
        deleteExisting: this.existingAttributeGroupMappingsIsDeleted,
        deleteExistingIncludeGlobals: this.existingGlobalMappingsIsDeleted,
        autoMappingScripts: selectedMappingScripts
      };
      await automappingApi.execute(mappingGeneratorSettings, this.language, this.$getConnectionId());
      this.createToast({
        text: this.$t("Automapping.AutomappingStartedForSelectedMappingProject"),
        title: this.$t("Automapping.AutoMappingStarted"),
        variant: "info",
        titleIcon: "Channel-Creator",
        textIllustration: "Processing"
      });

      this.clear();
    },
    createdMappingProject: async function (mappingProject) {
      if (mappingProject) {
        this.mappingProjectList.push(mappingProject);
        const sortKeyFinder = (x) => x?.name?.toUpperCase();
        this.mappingProjectList = _.sortBy(this.mappingProjectList, sortKeyFinder);
        this.mappingProject = mappingProject;
      }
    },
    init() {
      this.autoMappingCatalog = this.selectedTargetCatalog;
    },
    loadMappingProjects: async function () {
      if (!this.catalogId) {
        this.mappingProjectList = [];
        this.mappingProject = null;
      } else {
        if (this.catalogId == this.selectedTargetCatalogId) {
          this.mappingProjectList = _.cloneDeep(this.mappingProjects);
          this.mappingProject = this.selectedMappingProject;
        } else {
          const projects = await catalogApi.getCatalogMappingProjects(this.catalogId, this.language);
          let selectedCatalogProjectList = projects
            .map((x) =>
              x.mappingProjects.map((y) => ({
                catalogMappingProjectId: y.catalogMappingProjectId,
                catalogMappingTargetId: x.catalogMappingTargetId,
                name: y.name + " (" + x.targetSystemName + ")",
                targetSystemName: x.targetSystemName,
                targetSystem: x.targetSystem
              }))
            )
            .flat(1);
          const sortKeyFinder = (x) => x?.name?.toUpperCase();
          selectedCatalogProjectList = _.sortBy(selectedCatalogProjectList, sortKeyFinder);
          this.mappingProjectList = selectedCatalogProjectList;
          this.mappingProject = this.mappingProjectList.length > 0 ? this.mappingProjectList[0] : null;
        }
      }
    },
    getMappingScriptDetails: async function () {
      if (this.mappingProject.targetSystem) {
        const scriptDetails = await automappingApi.getAutoMappingScriptDetails(this.mappingProject.targetSystem);
        this.autoMappingDetails = _.cloneDeep(scriptDetails);
        this.autoMappingDetails = this.autoMappingDetails.map((obj) => ({
          ...obj,
          selectedMappingScript: obj.defaultMappingScriptName
        }));
      }
    },
    getLocalizedValue(key) {
      if (this.$te(key)) return this.$t(key);
      else return "";
    },
    selectMappingScript(autoMappingScriptDetail, scriptName) {
      autoMappingScriptDetail.selectedMappingScript = scriptName;
    },
    clear() {
      this.autoMappingCatalog = null;
      this.mappingProject = null;
      this.existingAttributeGroupMappingsIsDeleted = false;
      this.existingGlobalMappingsIsDeleted = false;
      this.autoMappingDetails = {};
    },
    confirmDelete(nv) {
      if (nv == true) {
        if (confirm(this.$t("Automapping.CatalogFieldMappingsDeleteExistingMessage"))) {
          this.existingAttributeGroupMappingsIsDeleted = true;
        } else {
          this.existingAttributeGroupMappingsIsDeleted = false;
        }
      }
    },
    getCatalogName(catalog) {
      return catalog[this.language]?.name;
    },
    getSortedMappingScriptNames(listToSort) {
      return naturalSort(listToSort, (x) => this.$t("Automapping.MappingScript_" + x.toLowerCase()));
    }
  },
  watch: {
    autoMappingCatalog: {
      immediate: true,
      deep: true,
      handler: async function (nv) {
        if (nv) {
          await this.loadMappingProjects();
        }
      }
    },
    mappingProject: {
      immediate: true,
      deep: true,
      handler: async function (nv) {
        if (nv) {
          await this.getMappingScriptDetails();
        }
      }
    }
  }
};
</script>
